import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { MetricType } from '../types';

let supabase: SupabaseClient | null = null;
let appName = '';

export function initSupabase(url: string, anonKey: string, app: string) {
  if (supabase || !url || !anonKey) return;

  // Validate URL format before calling createClient to avoid crashing during
  // Next.js static generation when placeholder env vars are in use.
  try {
    new URL(url);
  } catch {
    console.warn('[metrics] Skipping Supabase init: invalid supabaseUrl', url);
    return;
  }

  try {
    supabase = createClient(url, anonKey);
    appName = app;
  } catch (err) {
    console.warn('[metrics] Supabase client init failed:', err);
  }
}

export async function recordMetric(
  metricType: MetricType,
  value: number,
  metadata?: Record<string, any>
) {
  if (!supabase) return;

  try {
    await supabase.from('app_metrics').insert({
      app_name: appName,
      metric_type: metricType,
      value,
      metadata: metadata || {},
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to record metric:', error);
  }
}

export async function getMetrics(
  metricType?: MetricType,
  startDate?: Date,
  endDate?: Date
) {
  if (!supabase) return [];

  try {
    let query = supabase
      .from('app_metrics')
      .select('*')
      .eq('app_name', appName)
      .order('timestamp', { ascending: false });

    if (metricType) {
      query = query.eq('metric_type', metricType);
    }

    if (startDate) {
      query = query.gte('timestamp', startDate.toISOString());
    }

    if (endDate) {
      query = query.lte('timestamp', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch metrics:', error);
    return [];
  }
}

export async function getAppHealth() {
  if (!supabase) return null;

  try {
    // Get error count in last 24 hours
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    const { data, error } = await supabase
      .from('app_metrics')
      .select('value')
      .eq('app_name', appName)
      .eq('metric_type', 'error')
      .gte('timestamp', twentyFourHoursAgo.toISOString());

    if (error) throw error;

    const errorCount = data?.length || 0;
    const status = errorCount === 0 ? 'healthy' : errorCount < 10 ? 'warning' : 'critical';

    return {
      appName,
      errorCount,
      status,
      lastChecked: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Failed to get app health:', error);
    return null;
  }
}
